/* eslint-disable no-console */
import { geocodeByAddress, geocodeByPlaceId } from "react-places-autocomplete";
import { getTimeZone } from "./location";
import { getZip, getAddressFromComponents } from "../shared/utils/maps";

export type Address = {
  location: string;
  coordinates: { lat?: number; lng?: number };
  placeId: string;
  zipCode: string;
  street: string;
  city: string;
  state: string;
  county: string;
  timeZone: string;
};

export const addressNull: Address = {
  location: "",
  coordinates: {},
  placeId: "",
  zipCode: "",
  street: "",
  city: "",
  state: "",
  county: "",
  timeZone: ""
};

export const defaultSFAddress = {
  city: "San Francisco",
  coordinates: {
    lat: 37.7749295,
    lng: -122.4194155
  },
  county: "San Francisco County",
  location: "SF, CA, USA",
  placeId: "ChIJIQBpAG2ahYAR_6128GcTUEo",
  state: "California",
  street: "",
  timeZone: "America/Los_Angeles"
};

export const getFormattedAddress = async (dynamicAddress: string) => {
  try {
    const [result] = await geocodeByAddress(dynamicAddress);
    const address = formatAddress(result);
    return {
      ...address,
      zip: address.zipCode || (await getZip({ lat: address.coordinates.lat, lng: address.coordinates.lng }))
    };
  } catch (error) {
    console.log("Error getFormattedAddress: ", error);
    return addressNull;
  }
};

export const formatAddress = (result: { formatted_address: string; address_components: Array<any>; place_id: string; geometry: any }): Address => {
  try {
    const { street, city, state, zip, county } = getAddressFromComponents(result.address_components);
    const placeId = result.place_id;
    const coordinates = {
      lat: result.geometry.location.lat(),
      lng: result.geometry.location.lng()
    };
    const zipCode = zip;
    const timeZone = getTimeZone(coordinates);

    return {
      location: result.formatted_address,
      coordinates,
      placeId,
      zipCode,
      city,
      street,
      state,
      county,
      timeZone
    };
  } catch (error) {
    return addressNull;
  }
};

type FormatOption = (loc: any) => {
  label: string;
  optionLabel?: string;
  group?: string;
  value: any;
};

export const getMapsPlacePredictions = async ({
  address,
  formatOption = (loc: any) => ({ label: loc.formatted_address, value: loc })
}: {
  address: string;
  formatOption?: FormatOption;
}) => {
  if (window.google) {
    return new Promise<Array<any>>((resolve, reject) => {
      try {
        new google.maps.places.AutocompleteService().getPlacePredictions(
          {
            input: address,
            componentRestrictions: { country: ["us"] }
          },
          async (res: any) => {
            if (res?.length) {
              const result = await Promise.all(res.map(async (loc: any) => (await geocodeByPlaceId(loc.place_id))[0]));
              resolve(result.map(formatOption));
            } else {
              resolve([]);
            }
          }
        );
      } catch (error) {
        reject([]);
      }
    });
  } else {
    return [];
  }
};

export const getFomattedAddressLocation = (address: Address, redacted = false) => {
  const maskStreetNumber = (street: string) => {
    const containsDigits = /\d/.test(street);

    if (containsDigits) {
      return street.replace(/\d+/g, (match) => {
        return "***";
      });
    } else {
      const parts = street.split(" ");
      if (parts.length > 1) {
        return `*** ${parts.slice(1).join(" ")}`;
      }

      return street;
    }
  };
  if (redacted) {
    const redactedStreet = maskStreetNumber(address.street);
    return `${redactedStreet}, ${address.city}, ${address.zipCode}`;
  }

  return `${address.street}, ${address.city}, ${address.zipCode}`;
};
